export type JanusIceServer = {
  urls: string | string[];
  username?: string;
  credential?: string;
};

export type JanusJoinConfig = {
  wsUrl: string;
  apiSecret?: string | null;
  roomId: string | number;
  pin: string;
  iceServers: JanusIceServer[];
  displayName?: string;
};

export type JanusSessionHandles = {
  localStream: MediaStream;
  stop: () => void;
};

type JanusMessage = Record<string, unknown>;

function transactionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Call this directly from a click handler so the browser shows the permission prompt. */
export async function requestCallMedia(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera/microphone are not available in this browser');
  }

  const attempts: MediaStreamConstraints[] = [
    { audio: true, video: true },
    { audio: true, video: { facingMode: 'user' } },
    { audio: true, video: false },
  ];

  let lastError: unknown;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastError = err;
    }
  }

  const name =
    lastError && typeof lastError === 'object' && 'name' in lastError
      ? String((lastError as { name?: string }).name)
      : '';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    throw new Error(
      'No camera or microphone found. Plug in a device or check macOS System Settings → Privacy.'
    );
  }
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    throw new Error(
      'Camera/microphone permission denied. Allow access for this site and try again.'
    );
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    throw new Error('Camera/microphone is busy in another app. Close it and try again.');
  }
  if (lastError instanceof Error && lastError.message) {
    throw new Error(lastError.message);
  }
  throw new Error('Could not access camera or microphone');
}

export async function joinJanusVideoRoom(
  config: JanusJoinConfig,
  onRemoteStream: (stream: MediaStream) => void,
  existingLocalStream?: MediaStream
): Promise<JanusSessionHandles> {
  const localStream = existingLocalStream ?? (await requestCallMedia());

  const ws = new WebSocket(config.wsUrl, 'janus-protocol');
  await new Promise<void>((resolve, reject) => {
    ws.onopen = () => resolve();
    ws.onerror = () => reject(new Error('Failed to connect to Janus'));
  });

  let sessionId: number | null = null;
  let handleId: number | null = null;
  let pc: RTCPeerConnection | null = null;
  let stopped = false;
  const pending = new Map<
    string,
    { resolve: (v: JanusMessage) => void; reject: (e: Error) => void }
  >();
  const remoteStreams = new Map<number, MediaStream>();

  const send = (msg: JanusMessage) => {
    const tx = typeof msg.transaction === 'string' ? msg.transaction : transactionId();
    const payload: JanusMessage = { ...msg, transaction: tx };
    if (config.apiSecret) payload.apisecret = config.apiSecret;
    return new Promise<JanusMessage>((resolve, reject) => {
      pending.set(tx, { resolve, reject });
      ws.send(JSON.stringify(payload));
      setTimeout(() => {
        if (pending.has(tx)) {
          pending.delete(tx);
          reject(new Error('Janus request timed out'));
        }
      }, 12_000);
    });
  };

  ws.onmessage = async (ev) => {
    let data: JanusMessage;
    try {
      data = JSON.parse(String(ev.data)) as JanusMessage;
    } catch {
      return;
    }

    const tx = typeof data.transaction === 'string' ? data.transaction : null;
    if (tx && pending.has(tx)) {
      const waiter = pending.get(tx)!;
      pending.delete(tx);
      if (data.janus === 'error') {
        const err = data.error as { reason?: string } | undefined;
        waiter.reject(new Error(err?.reason || 'Janus error'));
      } else {
        waiter.resolve(data);
      }
    }

    if (data.janus === 'event' && data.plugindata) {
      const plugin = data.plugindata as {
        data?: {
          videoroom?: string;
          publishers?: Array<{ id: number; display?: string }>;
          leaving?: number | 'ok';
          unpublished?: number | 'ok';
        };
      };
      const body = plugin.data;
      if (body?.videoroom === 'event' && Array.isArray(body.publishers)) {
        for (const pub of body.publishers) {
          if (stopped) return;
          void attachSubscriber(pub.id, pub.display);
        }
      }
    }

    if (data.janus === 'event' && data.jsep && data.sender === handleId && pc) {
      const jsep = data.jsep as RTCSessionDescriptionInit;
      try {
        await pc.setRemoteDescription(jsep);
      } catch (err) {
        console.error('Failed to set remote description', err);
      }
    }
  };

  const createSession = await send({ janus: 'create' });
  sessionId = (createSession.data as { id: number }).id;

  const attached = await send({
    janus: 'attach',
    session_id: sessionId,
    plugin: 'janus.plugin.videoroom',
  });
  handleId = (attached.data as { id: number }).id;

  pc = new RTCPeerConnection({ iceServers: config.iceServers });
  for (const track of localStream.getTracks()) {
    pc.addTrack(track, localStream);
  }

  pc.onicecandidate = (ev) => {
    if (!ev.candidate || sessionId == null || handleId == null || stopped) return;
    void send({
      janus: 'trickle',
      session_id: sessionId,
      handle_id: handleId,
      candidate: ev.candidate.toJSON(),
    }).catch(() => undefined);
  };

  const joined = await send({
    janus: 'message',
    session_id: sessionId,
    handle_id: handleId,
    body: {
      request: 'join',
      room: Number(config.roomId),
      ptype: 'publisher',
      display: config.displayName || 'Milora',
      pin: config.pin,
    },
  });

  const joinPlugin = joined.plugindata as
    | { data?: { videoroom?: string; error?: string } }
    | undefined;
  if (joinPlugin?.data?.error) {
    stop();
    throw new Error(joinPlugin.data.error);
  }

  const existing =
    (joinPlugin?.data as { publishers?: Array<{ id: number; display?: string }> } | undefined)
      ?.publishers || [];
  for (const pub of existing) {
    void attachSubscriber(pub.id, pub.display);
  }

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  const configured = await send({
    janus: 'message',
    session_id: sessionId,
    handle_id: handleId,
    body: { request: 'configure', audio: true, video: true },
    jsep: offer,
  });
  const answerJsep = configured.jsep as RTCSessionDescriptionInit | undefined;
  if (answerJsep && pc) {
    await pc.setRemoteDescription(answerJsep);
  }

  async function attachSubscriber(feedId: number, _display?: string) {
    if (stopped || sessionId == null || remoteStreams.has(feedId)) return;
    const subAttach = await send({
      janus: 'attach',
      session_id: sessionId,
      plugin: 'janus.plugin.videoroom',
    });
    const subHandleId = (subAttach.data as { id: number }).id;
    const subPc = new RTCPeerConnection({ iceServers: config.iceServers });
    const remote = new MediaStream();
    remoteStreams.set(feedId, remote);

    subPc.ontrack = (ev) => {
      for (const track of ev.streams[0]?.getTracks() || [ev.track]) {
        remote.addTrack(track);
      }
      onRemoteStream(remote);
    };

    subPc.onicecandidate = (ev) => {
      if (!ev.candidate || sessionId == null || stopped) return;
      void send({
        janus: 'trickle',
        session_id: sessionId,
        handle_id: subHandleId,
        candidate: ev.candidate.toJSON(),
      }).catch(() => undefined);
    };

    const subJoined = await send({
      janus: 'message',
      session_id: sessionId,
      handle_id: subHandleId,
      body: {
        request: 'join',
        room: Number(config.roomId),
        ptype: 'subscriber',
        feed: feedId,
        pin: config.pin,
      },
    });

    const subJsep = subJoined.jsep as RTCSessionDescriptionInit | undefined;
    if (subJsep) {
      await subPc.setRemoteDescription(subJsep);
      const answer = await subPc.createAnswer();
      await subPc.setLocalDescription(answer);
      await send({
        janus: 'message',
        session_id: sessionId,
        handle_id: subHandleId,
        body: { request: 'start' },
        jsep: answer,
      });
    }
  }

  const keepAlive = setInterval(() => {
    if (stopped || sessionId == null) return;
    void send({ janus: 'keepalive', session_id: sessionId }).catch(() => undefined);
  }, 25_000);

  function stop() {
    if (stopped) return;
    stopped = true;
    clearInterval(keepAlive);
    for (const waiter of pending.values()) {
      waiter.reject(new Error('Janus session stopped'));
    }
    pending.clear();
    try {
      pc?.close();
    } catch {
      /* ignore */
    }
    pc = null;
    for (const track of localStream.getTracks()) track.stop();
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }

  return { localStream, stop };
}
